import type { MaskedPlayer } from '@completto/shared';
import { LANE_SIZE, laneRevealedCount } from '@completto/shared';
import { html, View } from 'rune-ts';

export type PlayerScoreListData = {
  players: readonly MaskedPlayer[];
  myPlayerId: string;
  winnerId: string | null;
  /** 꼴찌부터 1등 순서로 등장. 시작 지연(ms). 미지정 시 stagger 없음. */
  staggerStartMs?: number;
};

type ScoreRow = {
  player: MaskedPlayer;
  revealed: number;
  rank: number;
};

const STAGGER_STEP_MS = 150;

export class PlayerScoreListView extends View<PlayerScoreListData> {
  override template() {
    const { myPlayerId, winnerId, staggerStartMs } = this.data;
    const rows = this.computeRows();
    const total = rows.length;

    const items = rows.map((row) => {
      const { player, revealed, rank } = row;
      const isMe = player.id === myPlayerId;
      const isWinner = player.id === winnerId;
      const ringCls = isWinner ? 'ring-2 ring-card-gold' : 'ring-1 ring-wood-sand';
      const meBadge = isMe ? html`<span class="ml-1 text-xs text-emerald-600">[나]</span>` : html``;
      const winBadge = isWinner
        ? html`<span class="ml-1 text-xs text-card-brass">🏆</span>`
        : html``;
      const rankIcon = formatRank(rank);

      const staggerCls = staggerStartMs !== undefined ? 'result-score-row' : '';
      // rank=1(1등)이 가장 늦게 등장하도록 인덱스 역전.
      const delayMs =
        staggerStartMs !== undefined ? staggerStartMs + (total - rank) * STAGGER_STEP_MS : 0;
      const styleAttr = staggerStartMs !== undefined ? `--score-delay: ${delayMs}ms;` : '';

      return html`
        <li
          class="flex items-center justify-between rounded-xl bg-wood-paper ${ringCls} px-4 py-3 ${staggerCls}"
          style="${styleAttr}"
        >
          <span class="flex items-center gap-2">
            <span class="text-base font-bold text-wood-coffee w-6">${rankIcon}</span>
            <span class="text-base text-wood-ink">${player.nickname}${meBadge}${winBadge}</span>
          </span>
          <span class="text-sm tabular-nums text-wood-coffee">
            ${revealed}/${LANE_SIZE}
          </span>
        </li>
      `;
    });

    return html`
      <ol class="player-score-list flex flex-col gap-2">
        ${items}
      </ol>
    `;
  }

  private computeRows(): ScoreRow[] {
    const scored = this.data.players.map((player) => ({
      player,
      revealed: laneRevealedCount(player.lane),
    }));
    scored.sort((a, b) => {
      if (b.revealed !== a.revealed) return b.revealed - a.revealed;
      return a.player.nickname.localeCompare(b.player.nickname);
    });
    return scored.map((row, idx) => ({ ...row, rank: idx + 1 }));
  }
}

function formatRank(rank: number): string {
  if (rank === 1) return '1';
  if (rank === 2) return '2';
  if (rank === 3) return '3';
  return `${rank}`;
}
